import React from 'react';
import {Button, Icon} from '@lightricks/react-design-system';
import {
  CONTENT_SUBMISSION_ANALYTICS_BUTTONS as ANALYTICS_BUTTONS,
  CONTENT_SUBMISSION_ANALYTICS_SCREENS as ANALYTICS_SCREENS,
} from '@/lib/analytics/analyticsConstants';
import translate from '@/utils/translate';
import {
  ContentSubmission,
  ContentSubmissionVersion,
} from '@/types/contentSubmission';
import ContentSubmissionAnalysisModal from '@/components/content-submission-analysis-modal';
import DownloadButton from '@/components/download-button/DownloadButton';
import {buildDownloadFileName} from '@/hooks/use-download/utils';
import styles from './ContentSubmissionActionsFooter.module.scss';
import useContentSubmissionActionsFooterState from './useContentSubmissionActionsFooterState';

export interface ContentSubmissionActionsFooterProps {
  testID?: string;
  version: ContentSubmissionVersion;
  contentSubmission: ContentSubmission;
  isOffPlatform: boolean;
  isAdmin?: boolean;
  onAction?: (
    actionName: string,
    context: {campaign_id?: string; creator_id?: string; flow_id?: string}
  ) => void;
}

function ContentSubmissionActionsFooter({
  testID = 'content-submission-actions-footer',
  version,
  contentSubmission,
  isOffPlatform,
  isAdmin,
  onAction,
}: ContentSubmissionActionsFooterProps) {
  const actionContext = {
    campaign_id: contentSubmission.campaign?.id,
    creator_id: contentSubmission.profile?.id,
    flow_id: contentSubmission.id,
  };

  const {
    showApprove,
    showRequestEdits,
    showArchive,
    showUnarchive,
    showShare,
    showDownload,
    analysisButton,
    actionComponent,
    handleApproveClick,
    handleRequestEditClick,
    handleArchiveButtonClick,
    handleUnarchiveButtonClick,
    handleAiAnalysis,
    handleShareClick,
  } = useContentSubmissionActionsFooterState({
    contentSubmission,
    version,
    isAdmin,
    screenName: ANALYTICS_SCREENS.ASSET_DETAIL,
  });

  const {downloadUrl} = version;
  const hasMultipleVersions = (contentSubmission.versionCount ?? 0) > 1;
  const versionNumber = hasMultipleVersions
    ? [...(contentSubmission.versions ?? [])]
        .sort(
          (a, b) =>
            new Date(a.submittedAt ?? 0).getTime() -
            new Date(b.submittedAt ?? 0).getTime()
        )
        .findIndex((v) => v.id === version.id) + 1 || undefined
    : undefined;
  const downloadFileName = downloadUrl
    ? buildDownloadFileName(contentSubmission, downloadUrl, versionNumber)
    : undefined;

  if (isOffPlatform) {
    return null;
  }

  return (
    <>
      <div className={styles.footer} data-testid={testID}>
        {showApprove ? (
          <Button
            appearance="neutral"
            mode="plain"
            size="small"
            startIcon={
              <Icon
                name="Actions-Patch-Accept-Line"
                size="small"
                appearance="neutral"
                color="currentColor"
              />
            }
            onClick={() => {
              onAction?.(ANALYTICS_BUTTONS.ACTION_APPROVE, actionContext);
              handleApproveClick();
            }}
          >
            {translate('Approve')}
          </Button>
        ) : null}
        {showRequestEdits ? (
          <Button
            appearance="neutral"
            mode="plain"
            size="small"
            startIcon={
              <Icon
                name="Navigation-Edit"
                size="small"
                appearance="neutral"
                color="currentColor"
              />
            }
            onClick={() => {
              onAction?.(ANALYTICS_BUTTONS.ACTION_REQUEST_EDITS, actionContext);
              handleRequestEditClick();
            }}
          >
            {translate('Request Edits')}
          </Button>
        ) : null}
        {showArchive ? (
          <Button
            appearance="neutral"
            mode="plain"
            size="small"
            startIcon={
              <Icon
                name="Features-Reject"
                size="small"
                appearance="neutral"
                color="currentColor"
              />
            }
            onClick={() => {
              onAction?.(ANALYTICS_BUTTONS.ACTION_ARCHIVE, actionContext);
              handleArchiveButtonClick();
            }}
          >
            {translate('Archive')}
          </Button>
        ) : null}
        {showUnarchive ? (
          <Button
            appearance="neutral"
            mode="plain"
            size="small"
            startIcon={
              <Icon
                name="Actions-Reload-Large"
                size="small"
                appearance="neutral"
                color="currentColor"
              />
            }
            onClick={() => {
              onAction?.(ANALYTICS_BUTTONS.ACTION_UNARCHIVE, actionContext);
              handleUnarchiveButtonClick();
            }}
          >
            {translate('Unarchive')}
          </Button>
        ) : null}
        {analysisButton.show ? (
          <Button
            appearance="neutral"
            mode="plain"
            size="small"
            disabled={analysisButton.disabled}
            startIcon={
              <Icon
                name="Features-Effects"
                size="small"
                appearance="neutral"
                color="currentColor"
              />
            }
            onClick={() => {
              onAction?.(
                analysisButton.isViewAction
                  ? ANALYTICS_BUTTONS.ACTION_VIEW_AI_ANALYSIS
                  : ANALYTICS_BUTTONS.ACTION_RUN_AI_ANALYSIS,
                actionContext
              );
              handleAiAnalysis();
            }}
          >
            {analysisButton.label}
          </Button>
        ) : null}
        {showShare ? (
          <Button
            appearance="neutral"
            mode="plain"
            size="small"
            startIcon={
              <Icon
                name="Actions-Share"
                size="small"
                appearance="neutral"
                color="currentColor"
              />
            }
            onClick={() => {
              onAction?.(ANALYTICS_BUTTONS.ACTION_SHARE, actionContext);
              handleShareClick();
            }}
          >
            {translate('Share')}
          </Button>
        ) : null}
        {showDownload && downloadUrl ? (
          <DownloadButton
            id={contentSubmission.id}
            url={downloadUrl}
            fileName={downloadFileName}
          />
        ) : null}
      </div>
      <ContentSubmissionAnalysisModal />
      {actionComponent}
    </>
  );
}

export default ContentSubmissionActionsFooter;
